import { ComponentPropsWithoutRef } from "react";
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import { LucideIcon } from "lucide-react";
import { Input } from "@/components/ui";

type InputProps = ComponentPropsWithoutRef<typeof Input>;

export type InputWithIconProps<T extends FieldValues, TName extends Path<T>> = {
  field: ControllerRenderProps<T, TName>;
  icon: LucideIcon;
  placeholder?: string;
  className?: string;
  // Extend from InputProps but omit properties that we're handling internally
  inputProps?: Omit<
    InputProps,
    keyof ControllerRenderProps<T, TName> | "placeholder" | "className"
  >;
};

export const InputWithIcon = <T extends FieldValues, TName extends Path<T>>({
  field,
  icon: Icon,
  placeholder = "",
  className = "",
  inputProps = {},
}: InputWithIconProps<T, TName>) => {
  return (
    <div className="relative">
      <Icon
        className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        {...inputProps}
        {...field}
        placeholder={placeholder}
        className={`pl-9 ${className}`.trim()}
        aria-label={placeholder || field.name || "Input with icon"}
      />
    </div>
  );
};
